import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Grid, IconButton, Collapse, TextField, Alert, Dialog, Fade } from '@mui/material';
import {
  LocalShipping as TruckIcon,
  Check as CheckIcon,
  ArrowForward as ArrowIcon,
  ExpandMore as ExpandIcon,
  Dashboard as DashboardIcon,
  Speed as SpeedIcon,
  Receipt as InvoiceIcon,
  Visibility as VisibilityIcon,
  Handshake as HandshakeIcon,
  Close as CloseIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ContactDialog from '../components/ContactDialog';

/* === PALETTE === */
const C = {
  navBg: '#ffffff',
  heroBg: '#ffffff',
  sectionBg: '#f8fafc',
  cardBg: '#ffffff',
  cardBorder: '#e2e8f0',
  accent: '#2563eb',
  accentLight: '#3b82f6',
  accentBg: 'rgba(37,99,235,0.08)',
  text1: '#0f172a',
  text2: '#475569',
  text3: '#94a3b8',
  white: '#0f172a',
  gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
  gradientText: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
  success: '#22c55e',
  successBg: 'rgba(34,197,94,0.08)',
  danger: '#ef4444',
  divider: '#e2e8f0',
  orange: '#f59e0b',
  orangeBg: 'rgba(245,158,11,0.08)',
};

/* === NAV BAR === */
function NavBar({ onLogin, onTrial, onDashboard, isLoggedIn }) {
  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
    }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img src="/logo.png" alt="Cargo-Command TMS for Small Fleets" loading="eager" style={{ height: 160, width: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: C.white, letterSpacing: '-0.02em' }}>
              Cargo-Command
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 3 } }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              {['Features', 'Pricing', 'FAQ'].map(item => (
                <Typography key={item} component="a" href={`#${item.toLowerCase()}`}
                  sx={{ color: C.text2, fontSize: '0.88rem', fontWeight: 500, textDecoration: 'none', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: C.white } }}>
                  {item}
                </Typography>
              ))}
            </Box>
            {isLoggedIn ? (
              <Button variant="contained" onClick={onDashboard} startIcon={<DashboardIcon sx={{ fontSize: 18 }} />}
                sx={{ background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '0.88rem', borderRadius: '10px', px: 3, py: 0.9, boxShadow: '0 4px 20px rgba(37,99,235,0.3)', '&:hover': { boxShadow: '0 6px 28px rgba(37,99,235,0.45)', transform: 'translateY(-1px)' }, transition: 'all 0.2s' }}>
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button variant="text" onClick={onLogin}
                  sx={{ color: C.text2, textTransform: 'none', fontWeight: 600, fontSize: '0.88rem', '&:hover': { color: C.white, bgcolor: 'transparent' } }}>
                  Log In
                </Button>
                <Button variant="contained" onClick={onTrial}
                  sx={{ background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '0.88rem', borderRadius: '10px', px: 3, py: 0.9, boxShadow: '0 4px 20px rgba(37,99,235,0.3)', '&:hover': { boxShadow: '0 6px 28px rgba(37,99,235,0.45)', transform: 'translateY(-1px)' }, transition: 'all 0.2s' }}>
                  Start Free Trial
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

/* === FAQ ITEM === */
function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderBottom: `1px solid ${C.divider}`, py: 2.5, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: C.text1, pr: 2 }}>{question}</Typography>
        <IconButton size="small" sx={{ color: C.text3, transition: 'transform 0.3s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <ExpandIcon />
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Typography sx={{ color: C.text3, fontSize: '0.88rem', lineHeight: 1.8, mt: 1.5, pr: 6 }}>{answer}</Typography>
      </Collapse>
    </Box>
  );
}

/* === BULLET CHECK === */
function Chk({ children, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.6 }}>
      <CheckIcon sx={{ color: color || C.success, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '0.9rem', color: C.text2, lineHeight: 1.5 }}>{children}</Typography>
    </Box>
  );
}

/* === PAIN BULLET === */
function Pain({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5 }}>
      <CloseIcon sx={{ color: C.danger, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '0.92rem', color: C.text2, lineHeight: 1.5 }}>{children}</Typography>
    </Box>
  );
}

/* ===============================
   LANDING PAGE
   =============================== */
export default function Landing() {
  const navigate = useNavigate();
  const { user, login: doLogin, register: doRegister } = useAuth();
  const isLoggedIn = !!user;
  const goDashboard = () => navigate('/dashboard');
  const [planPrices, setPlanPrices] = useState({ core: 149, growth: 199, dispatcher: 299 });

  // --- Auth dialog state ---
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('register'); // 'login' | 'register'
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [regForm, setRegForm] = useState({ name: '', email: '', password: '', company: '' });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const goLogin = () => { setAuthMode('login'); setAuthError(''); setAuthOpen(true); };
  const goRegister = () => { setAuthMode('register'); setAuthError(''); setAuthOpen(true); };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      if (authMode === 'login') {
        const data = await doLogin(loginForm.email, loginForm.password);
        navigate(data.user?.role === 'masterAdmin' ? '/admin' : '/dashboard');
      } else {
        await doRegister(regForm);
        navigate('/dashboard');
      }
    } catch (err) {
      setAuthError(err.response?.data?.message || (authMode === 'login' ? 'Failed to sign in' : 'Failed to create account'));
    } finally {
      setAuthLoading(false);
    }
  };

  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const openContact = (subject) => { setContactSubject(subject); setContactOpen(true); };

  useEffect(() => {
    fetch('/api/contact/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: 'Landing', referrer: document.referrer }),
    }).catch(() => {});

    fetch('/api/settings/public-pricing')
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.prices) setPlanPrices(data.prices);
      })
      .catch(() => {});
  }, []);

  return (
    <Box sx={{ bgcolor: C.heroBg, minHeight: '100vh', overflowX: 'hidden' }}>
      <NavBar onLogin={goLogin} onTrial={goRegister} onDashboard={goDashboard} isLoggedIn={isLoggedIn} />

      {/* ==========================================
          HERO -- Who it's for + what it solves in 3 seconds
          ========================================== */}
      <Box sx={{
        pt: { xs: 16, md: 22 }, pb: { xs: 10, md: 16 },
        textAlign: 'center', position: 'relative',
        '&::before': {
          content: '""', position: 'absolute', top: '30%', left: '50%',
          transform: 'translate(-50%, -50%)', width: '80%', height: '60%',
          background: 'radial-gradient(ellipse, rgba(37,99,235,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h1" sx={{
            fontWeight: 800, fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem' },
            color: C.white, lineHeight: 1.1, letterSpacing: '-0.03em', mb: 3,
          }}>
            Transportation Management System for Small Fleets &mdash;{' '}
            <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Dispatch Software for 1-20 Trucks
            </Box>
          </Typography>
          <Typography component="p" sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' }, color: C.text2, lineHeight: 1.7, mb: 5, maxWidth: 620, mx: 'auto' }}>
            Affordable trucking TMS built for small carriers and owner-operators. Dispatch, invoicing, load tracking, and EDI &mdash; no enterprise complexity, no per-user pricing. Start your 14-day free trial today.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1.05rem',
                borderRadius: '12px', px: 5, py: 1.6,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s',
              }}>
              Start Free Trial (${planPrices.core}/mo)
            </Button>
            <Button variant="outlined" size="large"
              onClick={() => openContact('10-Min Demo Request')}
              sx={{
                borderColor: '#cbd5e1', color: C.text1,
                textTransform: 'none', fontWeight: 600, fontSize: '1.05rem',
                borderRadius: '12px', px: 5, py: 1.6,
                '&:hover': { borderColor: C.accent, bgcolor: C.accentBg },
                transition: 'all 0.3s',
              }}>
              Schedule a 10-Min Demo
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ==========================================
          PROBLEM + SOLUTION
          ========================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: C.sectionBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography component="h2" variant="h2" sx={{
              fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' },
              color: C.white, lineHeight: 1.15, mb: 2,
            }}>
              Small Fleet Dispatch System &mdash;{' '}
              <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Stop Losing Hours on Spreadsheets
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={6} alignItems="flex-start">
            {/* Pain Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px',
                p: { xs: 3.5, md: 4.5 },
              }}>
                <Typography sx={{ fontWeight: 700, color: C.text3, mb: 2.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.78rem' }}>
                  Running a small fleet means juggling
                </Typography>
                <Pain>Manual dispatch tracking in spreadsheets</Pain>
                <Pain>Invoices that take weeks to go out</Pain>
                <Pain>Missed broker status updates</Pain>
                <Pain>Lost driver communication</Pain>
                <Pain>Too many check calls eating your day</Pain>
              </Box>
            </Grid>

            {/* Solution Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: C.cardBg, border: `2px solid ${C.success}`, borderRadius: '20px',
                p: { xs: 3.5, md: 4.5 },
                boxShadow: '0 0 0 1px rgba(34,197,94,0.1), 0 12px 40px rgba(34,197,94,0.08)',
              }}>
                <Typography sx={{ fontWeight: 700, color: C.success, mb: 2.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.78rem' }}>
                  Cargo-Command solves this
                </Typography>
                <Chk>Central dispatch board - all loads, one screen</Chk>
                <Chk>Load tracking & real-time status updates</Chk>
                <Chk>Easy invoicing + billing on delivery</Chk>
                <Chk>Optional EDI connectivity with brokers</Chk>
                <Chk>No contracts, no per-user pricing</Chk>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="contained" size="large" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                borderRadius: '12px', px: 5, py: 1.5,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s',
              }}>
              Start Free Trial
            </Button>
            <Typography sx={{ color: C.text3, fontSize: '0.85rem', mt: 1.5 }}>
              Because dispatch shouldn't be harder than the job itself.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ==========================================
          BENEFITS + FEATURES -- 4 cards, scannable
          ========================================== */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 }, bgcolor: C.heroBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography sx={{
              fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.72rem',
              color: C.accent, textTransform: 'uppercase', mb: 1.5,
            }}>Dispatch Software for Small Trucking Companies</Typography>
            <Typography component="h2" variant="h2" sx={{
              fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' },
              color: C.white, lineHeight: 1.15,
            }}>
              Affordable Trucking TMS &mdash;{' '}
              <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Everything You Need, Nothing You Don't
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: <SpeedIcon sx={{ color: C.accentLight, fontSize: 28 }} />,
                title: 'Operational Simplicity',
                bullets: ['No confusing menus', 'Dashboard shows what matters now', 'Set up in minutes, not weeks'],
              },
              {
                icon: <InvoiceIcon sx={{ color: '#22c55e', fontSize: 28 }} />,
                title: 'Speed Up Billing',
                bullets: ['Create & send invoices fast', 'Auto-invoice on delivery', 'Get paid sooner'],
              },
              {
                icon: <VisibilityIcon sx={{ color: '#f59e0b', fontSize: 28 }} />,
                title: 'Clear Fleet Visibility',
                bullets: ['Know where your drivers are', 'Status updates sent automatically', 'One board for every load'],
              },
              {
                icon: <HandshakeIcon sx={{ color: '#a78bfa', fontSize: 28 }} />,
                title: 'Broker Friendly',
                bullets: ['Optional EDI support (204, 990, 214, 210)', 'Automated status sends', 'Professional invoices via email'],
              },
            ].map((card, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{
                  bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px',
                  p: 3.5, height: '100%', transition: 'all 0.3s',
                  '&:hover': { borderColor: C.accent, transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(37,99,235,0.12)' },
                }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '12px', bgcolor: C.accentBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    {card.icon}
                  </Box>
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.text1, mb: 2 }}>{card.title}</Typography>
                  {card.bullets.map((b, j) => (
                    <Box key={j} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.4 }}>
                      <CheckIcon sx={{ color: C.success, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.85rem', color: C.text3, lineHeight: 1.5 }}>{b}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="contained" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                borderRadius: '12px', px: 5, py: 1.5,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s',
              }}>
              Try It Free - 14-Day Trial
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ==========================================
          SCREENSHOTS / PRODUCT VISUALS
          ========================================== */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: C.sectionBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography component="h2" variant="h2" sx={{
              fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' },
              color: C.white, lineHeight: 1.15, mb: 2,
            }}>
              TMS for Owner Operators - See It in Action
            </Typography>
            <Typography sx={{ color: C.text3, fontSize: '1rem', maxWidth: 480, mx: 'auto' }}>
              A quick look at what you'll work with every day.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* ---- DISPATCH BOARD MOCKUP ---- */}
            <Grid item xs={12} sm={6}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px',
                p: 3, position: 'relative', overflow: 'hidden', height: '100%',
                transition: 'all 0.3s',
                '&:hover': { borderColor: C.accentLight, transform: 'translateY(-2px)' },
              }}>
                <Box sx={{ bgcolor: '#0f1a2e', borderRadius: '10px', overflow: 'hidden', mb: 2, border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  {/* Mini stats bar */}
                  <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.8, bgcolor: '#0f172a', borderBottom: '1px solid #1e293b', alignItems: 'center' }}>
                    {[{ l: 'Unassigned', c: '#64748b', n: 3 }, { l: 'Assigned', c: '#3b82f6', n: 5 }, { l: 'Dispatched', c: '#8b5cf6', n: 4 }, { l: 'Delivered', c: '#22c55e', n: 8 }].map(s => (
                      <Box key={s.l} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: s.c }} />
                        <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8' }}>{s.n}</Typography>
                      </Box>
                    ))}
                    <Typography sx={{ fontSize: '0.5rem', color: '#22c55e', ml: 'auto', fontWeight: 700 }}>$47,200</Typography>
                  </Box>
                  {/* Kanban columns */}
                  <Box sx={{ display: 'flex', gap: 0, minHeight: 145 }}>
                    {[
                      { title: 'Unassigned', color: '#64748b', loads: [
                        { id: 'LD-1042', from: 'Dallas, TX', to: 'Houston, TX', rate: '$2,800', status: 'new' },
                        { id: 'LD-1045', from: 'Austin, TX', to: 'El Paso, TX', rate: '$3,100', status: 'new' },
                      ]},
                      { title: 'Dispatched', color: '#8b5cf6', loads: [
                        { id: 'LD-1038', from: 'Memphis, TN', to: 'Nashville, TN', rate: '$1,950', driver: 'J. Smith', status: 'dispatched' },
                        { id: 'LD-1039', from: 'Atlanta, GA', to: 'Miami, FL', rate: '$4,200', driver: 'R. Johnson', status: 'at-pickup' },
                      ]},
                      { title: 'Delivered', color: '#22c55e', loads: [
                        { id: 'LD-1031', from: 'Chicago, IL', to: 'Detroit, MI', rate: '$2,400', driver: 'M. Davis', status: 'delivered' },
                        { id: 'LD-1033', from: 'LA, CA', to: 'Phoenix, AZ', rate: '$3,600', driver: 'T. Wilson', status: 'delivered' },
                      ]},
                    ].map((col, ci) => (
                      <Box key={ci} sx={{ flex: 1, borderRight: ci < 2 ? '1px solid #1e293b' : 'none', px: 0.8, py: 0.6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.6 }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: col.color }} />
                          <Typography sx={{ fontSize: '0.48rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{col.title}</Typography>
                          <Box sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 3, px: 0.4, ml: 'auto' }}>
                            <Typography sx={{ fontSize: '0.44rem', color: '#64748b' }}>{col.loads.length}</Typography>
                          </Box>
                        </Box>
                        {col.loads.map((ld, li) => (
                          <Box key={li} sx={{ bgcolor: '#1e293b', borderRadius: '4px', p: 0.6, mb: 0.5, borderLeft: `2px solid ${col.color}`, border: '1px solid #334155' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.2 }}>
                              <Typography sx={{ fontSize: '0.48rem', fontWeight: 700, color: '#f1f5f9' }}>{ld.id}</Typography>
                              <Typography sx={{ fontSize: '0.44rem', fontWeight: 700, color: '#22c55e' }}>{ld.rate}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.42rem', color: '#94a3b8' }}>{ld.from} &rarr; {ld.to}</Typography>
                            {ld.driver && <Typography sx={{ fontSize: '0.4rem', color: '#64748b', mt: 0.2 }}>{ld.driver}</Typography>}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: C.text1, mb: 0.5 }}>Dispatch Board</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: C.text3, lineHeight: 1.5 }}>Kanban-style load management - drag, drop, done.</Typography>
                <Box sx={{ position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: '50%', bgcolor: C.accentLight }} />
              </Box>
            </Grid>

            {/* ---- LOAD TRACKING MOCKUP ---- */}
            <Grid item xs={12} sm={6}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px',
                p: 3, position: 'relative', overflow: 'hidden', height: '100%',
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#f59e0b', transform: 'translateY(-2px)' },
              }}>
                <Box sx={{ bgcolor: '#0f1a2e', borderRadius: '10px', overflow: 'hidden', mb: 2, border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  {/* Status summary cards */}
                  <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.8, bgcolor: '#0f172a', borderBottom: '1px solid #1e293b' }}>
                    {[{ l: 'In Transit', n: 6, c: '#3b82f6' }, { l: 'At Pickup', n: 2, c: '#f59e0b' }, { l: 'Delivered', n: 12, c: '#22c55e' }, { l: 'Issues', n: 1, c: '#ef4444' }].map(s => (
                      <Box key={s.l} sx={{ flex: 1, bgcolor: '#1e293b', borderRadius: '4px', p: 0.5, textAlign: 'center', borderTop: `2px solid ${s.c}` }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: s.c }}>{s.n}</Typography>
                        <Typography sx={{ fontSize: '0.38rem', color: '#64748b' }}>{s.l}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Load tracking rows */}
                  <Box sx={{ px: 0.8, py: 0.5 }}>
                    {/* Table header */}
                    <Box sx={{ display: 'flex', gap: 0.5, px: 0.5, py: 0.3, borderBottom: '1px solid #1e293b' }}>
                      {['Load #', 'Route', 'Driver', 'Status', 'ETA'].map(h => (
                        <Typography key={h} sx={{ flex: h === 'Route' ? 2 : 1, fontSize: '0.38rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{h}</Typography>
                      ))}
                    </Box>
                    {[
                      { id: 'LD-1038', route: 'Memphis, TN → Nashville, TN', driver: 'J. Smith', status: 'In Transit', sc: '#3b82f6', eta: '2:30 PM' },
                      { id: 'LD-1039', route: 'Atlanta, GA → Miami, FL', driver: 'R. Johnson', status: 'At Pickup', sc: '#f59e0b', eta: '4:15 PM' },
                      { id: 'LD-1040', route: 'Dallas, TX → Denver, CO', driver: 'M. Davis', status: 'In Transit', sc: '#3b82f6', eta: '8:00 PM' },
                      { id: 'LD-1031', route: 'Chicago, IL → Detroit, MI', driver: 'T. Wilson', status: 'Delivered', sc: '#22c55e', eta: '--' },
                    ].map((row, ri) => (
                      <Box key={ri} sx={{ display: 'flex', gap: 0.5, px: 0.5, py: 0.4, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                        <Typography sx={{ flex: 1, fontSize: '0.42rem', fontWeight: 700, color: '#f1f5f9' }}>{row.id}</Typography>
                        <Typography sx={{ flex: 2, fontSize: '0.4rem', color: '#94a3b8' }}>{row.route}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.4rem', color: '#cbd5e1' }}>{row.driver}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, bgcolor: `${row.sc}18`, borderRadius: 3, px: 0.5, py: 0.15 }}>
                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: row.sc }} />
                            <Typography sx={{ fontSize: '0.38rem', color: row.sc, fontWeight: 600 }}>{row.status}</Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ flex: 1, fontSize: '0.4rem', color: '#94a3b8', textAlign: 'right' }}>{row.eta}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: C.text1, mb: 0.5 }}>Load Tracking</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: C.text3, lineHeight: 1.5 }}>Real-time status from pickup to delivery.</Typography>
                <Box sx={{ position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
              </Box>
            </Grid>

            {/* ---- INVOICING MOCKUP ---- */}
            <Grid item xs={12} sm={6}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px',
                p: 3, position: 'relative', overflow: 'hidden', height: '100%',
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#22c55e', transform: 'translateY(-2px)' },
              }}>
                <Box sx={{ bgcolor: '#0f1a2e', borderRadius: '10px', overflow: 'hidden', mb: 2, border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  {/* Aging summary */}
                  <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.8, bgcolor: '#0f172a', borderBottom: '1px solid #1e293b' }}>
                    {[{ l: '0-30 Days', v: '$12,400', c: '#22c55e' }, { l: '31-60', v: '$4,800', c: '#ea580c' }, { l: '61-90', v: '$2,100', c: '#f59e0b' }, { l: '90+', v: '$950', c: '#ef4444' }].map(a => (
                      <Box key={a.l} sx={{ flex: 1, bgcolor: '#1e293b', borderRadius: '4px', p: 0.5, borderLeft: `2px solid ${a.c}` }}>
                        <Typography sx={{ fontSize: '0.36rem', color: '#64748b', textTransform: 'uppercase' }}>{a.l}</Typography>
                        <Typography sx={{ fontSize: '0.54rem', fontWeight: 800, color: a.c }}>{a.v}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Invoice table */}
                  <Box sx={{ px: 0.8, py: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, px: 0.5, py: 0.3, borderBottom: '1px solid #1e293b' }}>
                      {['Invoice #', 'Customer', 'Amount', 'Status'].map(h => (
                        <Typography key={h} sx={{ flex: 1, fontSize: '0.38rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                    {[
                      { id: 'INV-2041', cust: 'ABC Logistics', amt: '$4,200', status: 'Paid', sc: '#22c55e' },
                      { id: 'INV-2042', cust: 'Swift Transport', amt: '$3,100', status: 'Pending', sc: '#f59e0b' },
                      { id: 'INV-2038', cust: 'FedEx Freight', amt: '$2,800', status: 'Overdue', sc: '#ef4444' },
                      { id: 'INV-2043', cust: 'XPO Logistics', amt: '$5,600', status: 'Paid', sc: '#22c55e' },
                    ].map((inv, ii) => (
                      <Box key={ii} sx={{ display: 'flex', gap: 0.5, px: 0.5, py: 0.4, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <Typography sx={{ flex: 1, fontSize: '0.42rem', fontWeight: 700, color: '#f1f5f9' }}>{inv.id}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.4rem', color: '#94a3b8' }}>{inv.cust}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.42rem', fontWeight: 700, color: '#f1f5f9', textAlign: 'right' }}>{inv.amt}</Typography>
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                          <Box sx={{ display: 'inline-flex', bgcolor: `${inv.sc}18`, borderRadius: 3, px: 0.5, py: 0.15 }}>
                            <Typography sx={{ fontSize: '0.38rem', color: inv.sc, fontWeight: 600 }}>{inv.status}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: C.text1, mb: 0.5 }}>Invoicing</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: C.text3, lineHeight: 1.5 }}>Auto-generate and email invoices on delivery.</Typography>
                <Box sx={{ position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
              </Box>
            </Grid>

            {/* ---- DRIVER STATUS MOCKUP ---- */}
            <Grid item xs={12} sm={6}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px',
                p: 3, position: 'relative', overflow: 'hidden', height: '100%',
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#a78bfa', transform: 'translateY(-2px)' },
              }}>
                <Box sx={{ bgcolor: '#0f1a2e', borderRadius: '10px', overflow: 'hidden', mb: 2, border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  {/* Driver summary */}
                  <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.8, bgcolor: '#0f172a', borderBottom: '1px solid #1e293b' }}>
                    {[{ l: 'Active', n: 8, c: '#22c55e' }, { l: 'Assigned', n: 5, c: '#3b82f6' }, { l: 'Available', n: 3, c: '#f59e0b' }, { l: 'Total', n: 10, c: '#f1f5f9' }].map(s => (
                      <Box key={s.l} sx={{ flex: 1, bgcolor: '#1e293b', borderRadius: '4px', p: 0.5, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: s.c }}>{s.n}</Typography>
                        <Typography sx={{ fontSize: '0.36rem', color: '#64748b' }}>{s.l}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Driver rows */}
                  <Box sx={{ px: 0.8, py: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, px: 0.5, py: 0.3, borderBottom: '1px solid #1e293b' }}>
                      {['Driver', 'Phone', 'Truck', 'Status'].map(h => (
                        <Typography key={h} sx={{ flex: 1, fontSize: '0.38rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{h}</Typography>
                      ))}
                    </Box>
                    {[
                      { name: 'James Smith', phone: '(615) 555-0142', truck: 'T-101', status: 'Active', sc: '#22c55e' },
                      { name: 'Robert Johnson', phone: '(423) 555-0198', truck: 'T-104', status: 'Active', sc: '#22c55e' },
                      { name: 'Michael Davis', phone: '(931) 555-0267', truck: 'T-107', status: 'On Leave', sc: '#f59e0b' },
                      { name: 'Thomas Wilson', phone: '(615) 555-0321', truck: 'T-112', status: 'Active', sc: '#22c55e' },
                    ].map((d, di) => (
                      <Box key={di} sx={{ display: 'flex', gap: 0.5, px: 0.5, py: 0.4, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <Typography sx={{ flex: 1, fontSize: '0.42rem', fontWeight: 700, color: '#f1f5f9' }}>{d.name}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.4rem', color: '#94a3b8' }}>{d.phone}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'inline-flex', bgcolor: '#172554', borderRadius: 3, px: 0.5, py: 0.15, alignItems: 'center', gap: 0.3 }}>
                            <Typography sx={{ fontSize: '0.38rem', color: '#3b82f6', fontWeight: 600 }}>{d.truck}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'inline-flex', bgcolor: `${d.sc}18`, borderRadius: 3, px: 0.5, py: 0.15 }}>
                            <Typography sx={{ fontSize: '0.38rem', color: d.sc, fontWeight: 600 }}>{d.status}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: C.text1, mb: 0.5 }}>Driver Status</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: C.text3, lineHeight: 1.5 }}>WhatsApp updates - no app download needed.</Typography>
                <Box sx={{ position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: '50%', bgcolor: '#a78bfa' }} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="outlined" size="large"
              onClick={() => openContact('Schedule Live Demo')}
              sx={{
                borderColor: '#cbd5e1', color: C.text1,
                textTransform: 'none', fontWeight: 600, fontSize: '1rem',
                borderRadius: '12px', px: 5, py: 1.4,
                '&:hover': { borderColor: C.accent, bgcolor: C.accentBg },
                transition: 'all 0.3s',
              }}>
              See It in Action - Schedule Live Demo
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ==========================================
          PRICING -- Simple + transparent
          ========================================== */}
      <Box id="pricing" sx={{ py: { xs: 8, md: 12 }, bgcolor: C.heroBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography component="h2" variant="h2" sx={{
              fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' },
              color: C.white, lineHeight: 1.15, mb: 2,
            }}>
              Affordable TMS Pricing for Small Fleets &mdash;{' '}
              <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                No Surprises
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* -- Core Plan -- */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px',
                p: { xs: 4, md: 5 }, height: '100%', transition: 'all 0.3s',
                '&:hover': { borderColor: C.accent, boxShadow: '0 12px 40px rgba(37,99,235,0.12)' },
              }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: C.white, mb: 1 }}>Core</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '3.2rem', color: C.white, lineHeight: 1 }}>$149</Typography>
                  <Typography sx={{ color: C.text3, fontSize: '1rem', fontWeight: 500 }}>/ month</Typography>
                </Box>
                <Typography sx={{ color: C.text3, fontSize: '0.9rem', mb: 3 }}>Up to 10 trucks</Typography>

                <Box sx={{ mb: 3 }}>
                  <Chk>Unlimited users</Chk>
                  <Chk>Dispatch & invoicing</Chk>
                  <Chk>Status updates</Chk>
                  <Chk>Basic reporting</Chk>
                  <Chk>Free onboarding</Chk>
                  <Chk>Cancel anytime</Chk>
                </Box>

                <Typography sx={{ color: C.text3, fontSize: '0.8rem', mb: 3, fontStyle: 'italic' }}>
                  No setup fees. No contracts.
                </Typography>

                <Button variant="contained" fullWidth size="large" onClick={goRegister}
                  sx={{
                    background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                    borderRadius: '12px', py: 1.5,
                    boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                    '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)' },
                  }}>
                  Start Free Trial
                </Button>
              </Box>
            </Grid>

            {/* -- Growth Plan -- */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                bgcolor: C.cardBg, borderRadius: '20px',
                p: { xs: 4, md: 5 }, height: '100%', position: 'relative',
                border: `2px solid ${C.accent}`,
                boxShadow: '0 0 0 1px rgba(37,99,235,0.2), 0 20px 60px rgba(37,99,235,0.15)',
                transition: 'all 0.3s',
                '&:hover': { boxShadow: '0 0 0 1px rgba(37,99,235,0.3), 0 24px 80px rgba(37,99,235,0.2)' },
              }}>
                <Box sx={{
                  position: 'absolute', top: -1, right: 32,
                  background: C.gradient, px: 2.5, py: 0.5, borderRadius: '0 0 10px 10px',
                }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Most Popular
                  </Typography>
                </Box>

                <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: C.white, mb: 1 }}>Growth</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '3.2rem', color: C.white, lineHeight: 1 }}>${planPrices.dispatcher}</Typography>
                  <Typography sx={{ color: C.text3, fontSize: '1rem', fontWeight: 500 }}>/ month</Typography>
                </Box>
                <Typography sx={{ color: C.text3, fontSize: '0.9rem', mb: 3 }}>11-20 trucks</Typography>

                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: C.accent, mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Everything in Core, plus:
                  </Typography>
                  <Chk color={C.accentLight}>Advanced automation</Chk>
                  <Chk color={C.accentLight}>EDI-ready capability</Chk>
                  <Chk color={C.accentLight}>Expanded reporting</Chk>
                  <Chk color={C.accentLight}>Priority support</Chk>
                </Box>

                <Typography sx={{ color: C.text3, fontSize: '0.8rem', mb: 3, fontStyle: 'italic' }}>
                  No setup fees. No contracts.
                </Typography>

                <Button variant="outlined" fullWidth size="large"
                  onClick={() => openContact('Growth Plan Inquiry')}
                  sx={{
                    borderColor: C.accent, color: C.accentLight,
                    textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                    borderRadius: '12px', py: 1.5,
                    '&:hover': { bgcolor: C.accentBg, borderColor: C.accentLight },
                  }}>
                  Talk to Us
                </Button>
              </Box>
            </Grid>

            {/* -- Dispatcher Plan -- */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px',
                p: { xs: 4, md: 5 }, height: '100%', position: 'relative',
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#f59e0b', boxShadow: '0 12px 40px rgba(245,158,11,0.12)' },
              }}>
                <Box sx={{
                  position: 'absolute', top: -1, right: 32,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', px: 2.5, py: 0.5, borderRadius: '0 0 10px 10px',
                }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Multi-Fleet
                  </Typography>
                </Box>

                <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: C.white, mb: 1 }}>Dispatcher</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '3.2rem', color: C.white, lineHeight: 1 }}>$299</Typography>
                  <Typography sx={{ color: C.text3, fontSize: '1rem', fontWeight: 500 }}>/ month</Typography>
                </Box>
                <Typography sx={{ color: C.text3, fontSize: '0.9rem', mb: 3 }}>Up to 25 trucks managed</Typography>

                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#f59e0b', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Built for dispatchers:
                  </Typography>
                  <Chk color="#fbbf24">Manage multiple carriers</Chk>
                  <Chk color="#fbbf24">Commission tracking</Chk>
                  <Chk color="#fbbf24">Full dispatch & invoicing</Chk>
                  <Chk color="#fbbf24">Route optimization</Chk>
                  <Chk color="#fbbf24">Priority support</Chk>
                </Box>

                <Typography sx={{ color: C.text3, fontSize: '0.8rem', mb: 3, fontStyle: 'italic' }}>
                  No setup fees. No contracts.
                </Typography>

                <Button variant="contained" fullWidth size="large" onClick={goRegister}
                  sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                    borderRadius: '12px', py: 1.5,
                    boxShadow: '0 8px 32px rgba(245,158,11,0.35)',
                    '&:hover': { boxShadow: '0 12px 44px rgba(245,158,11,0.5)' },
                  }}>
                  Start Free Trial
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* What's included summary */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Box sx={{
              display: 'inline-flex', flexWrap: 'wrap', gap: { xs: 2, md: 4 }, justifyContent: 'center',
            }}>
              {['Unlimited users', 'Dispatch & invoicing', 'Status updates', 'Basic reporting', 'Free onboarding', 'Cancel anytime'].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <CheckIcon sx={{ color: C.success, fontSize: 16 }} />
                  <Typography sx={{ color: C.text3, fontSize: '0.82rem' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* --- CTA BREAK --- */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: C.sectionBg }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography component="h2" variant="h2" sx={{
            fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' },
            color: C.white, lineHeight: 1.15, mb: 1.5,
          }}>
            Ready to Streamline Your Fleet?
          </Typography>
          <Typography sx={{ color: C.text2, fontSize: '1.05rem', mb: 1.5 }}>
            Free onboarding and setup support included — we'll help you get your trucks and drivers loaded quickly.
          </Typography>
          <Typography sx={{ color: C.accent, fontSize: '0.95rem', mb: 4, fontWeight: 600 }}>
            We customize the product to fit each customer's unique needs.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                borderRadius: '12px', px: 4, py: 1.3,
                boxShadow: '0 6px 24px rgba(37,99,235,0.3)',
                '&:hover': { boxShadow: '0 10px 36px rgba(37,99,235,0.45)', transform: 'translateY(-1px)' },
                transition: 'all 0.3s',
              }}>
              Start Free Trial
            </Button>
            <Button variant="outlined"
              onClick={() => openContact('Watch Demo')}
              sx={{
                borderColor: '#cbd5e1', color: C.text1,
                textTransform: 'none', fontWeight: 600, fontSize: '1rem',
                borderRadius: '12px', px: 4, py: 1.3,
                '&:hover': { borderColor: C.accent, bgcolor: C.accentBg },
                transition: 'all 0.3s',
              }}>
              Watch Demo
            </Button>
          </Box>
        </Container>
      </Box>


      {/* ==========================================================
          WHAT IS A TMS?
          ========================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: C.heroBg }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography sx={{ fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.72rem', color: C.accent, textTransform: 'uppercase', mb: 1.5 }}>Industry Fundamentals</Typography>
              <Typography component="h2" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: C.white, lineHeight: 1.15, mb: 3 }}>
                What Is a{' '}
                <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Transportation Management System?</Box>
              </Typography>
              <Typography sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85, mb: 2.5 }}>
                A Transportation Management System (TMS) is software that helps trucking companies plan, execute, and optimize the movement of freight. It centralizes dispatch, load tracking, invoicing, driver management, and compliance into a single platform &mdash; replacing scattered spreadsheets, phone calls, and paper processes.
              </Typography>
              <Typography sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85, mb: 2.5 }}>
                For large carriers, a TMS has been standard for decades. But most TMS platforms are built for enterprise fleets with 50 to 500+ trucks, making them too expensive and complicated for small operations. That is changing.
              </Typography>
              <Typography sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85 }}>
                Cargo-Command was purpose-built as a <Typography component="a" href="/tms-for-small-fleets" sx={{ color: C.accentLight, textDecoration: 'underline' }}>TMS for small fleets</Typography> &mdash; giving carriers with 1 to 20 trucks the same operational advantages that enterprise carriers have, at a price point that makes sense for smaller operations.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px', p: { xs: 3, md: 4 } }}>
                <Typography sx={{ fontWeight: 700, color: C.white, fontSize: '1.05rem', mb: 3 }}>What a TMS Replaces</Typography>
                <Grid container spacing={2}>
                  {[
                    { old: 'Spreadsheet dispatch tracking', neo: 'Central dispatch board with real-time status' },
                    { old: 'Manual invoicing in Word or Excel', neo: 'Auto-generated invoices on delivery' },
                    { old: 'Phone calls for driver check-ins', neo: 'Automated status updates via WhatsApp' },
                    { old: 'Sticky notes for load assignments', neo: 'One-click driver and truck assignment' },
                    { old: 'Email chains with brokers', neo: 'EDI integration for tenders and updates' },
                    { old: 'Paper-based HOS tracking', neo: 'Digital Hours of Service compliance' },
                  ].map((item, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ bgcolor: C.heroBg, borderRadius: '12px', p: 2, height: '100%', border: `1px solid ${C.cardBorder}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <CloseIcon sx={{ color: C.danger, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.82rem', color: C.text3, textDecoration: 'line-through' }}>{item.old}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CheckIcon sx={{ color: C.success, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.85rem', color: C.text1, fontWeight: 600 }}>{item.neo}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ==========================================================
          HOW DISPATCH SOFTWARE WORKS
          ========================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: C.sectionBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography sx={{ fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.72rem', color: C.accent, textTransform: 'uppercase', mb: 1.5 }}>Behind the Scenes</Typography>
            <Typography component="h2" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: C.white, lineHeight: 1.15 }}>
              How Dispatch Software{' '}
              <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Actually Works</Box>
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[
              { step: '1', title: 'Load Entry', desc: 'A new load comes in from a broker, shipper, or load board. You enter the pickup, delivery, rate, and freight details — or accept it via EDI automatically.', color: C.accent },
              { step: '2', title: 'Driver Assignment', desc: 'The dispatch board shows available drivers and trucks. Assign the load with one click based on location, Hours of Service, and equipment type.', color: '#22c55e' },
              { step: '3', title: 'Real-Time Tracking', desc: 'As the driver picks up and delivers, status updates are logged automatically. Brokers receive notifications without you making a single phone call.', color: '#f59e0b' },
              { step: '4', title: 'Automatic Invoicing', desc: 'The moment a load is marked delivered, Cargo-Command generates a professional invoice and emails it to the customer. No delays, no forgotten loads.', color: '#a78bfa' },
            ].map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px', p: 3.5, height: '100%', position: 'relative', transition: 'all 0.3s', '&:hover': { borderColor: s.color, transform: 'translateY(-4px)', boxShadow: `0 12px 40px ${s.color}18` } }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: s.color }}>{s.step}</Typography>
                  </Box>
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.text1, mb: 1.5 }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: C.text3, lineHeight: 1.6 }}>{s.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography sx={{ color: C.text2, fontSize: '0.95rem', maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
              From load entry to payment, Cargo-Command keeps everything in one place &mdash; so you spend less time on admin and more time moving freight.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ==========================================================
          WHY SMALL FLEETS NEED AUTOMATION
          ========================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: C.heroBg }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.72rem', color: C.accent, textTransform: 'uppercase', mb: 1.5 }}>The Small Fleet Advantage</Typography>
              <Typography component="h2" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: C.white, lineHeight: 1.15, mb: 3 }}>
                Why Small Fleets Need{' '}
                <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Automation Now</Box>
              </Typography>
              <Typography sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85, mb: 2.5 }}>
                Small carriers compete against fleets with dedicated IT departments and enterprise software. Without automation, you are spending hours on tasks that technology can handle in seconds &mdash; and that time gap costs real money.
              </Typography>
              <Typography sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85, mb: 2.5 }}>
                The trucking industry is tightening. Brokers expect faster status updates, shippers want real-time visibility, and drivers expect modern tools. Fleets that still rely on manual processes are losing loads to carriers that can respond faster and operate more efficiently.
              </Typography>
              <Typography sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85 }}>
                Automation is not about replacing your team &mdash; it is about giving a 3-person office the operational power of a 10-person team. That is exactly what Cargo-Command delivers.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { stat: '4+ hrs', label: 'Saved per day on dispatch and invoicing tasks', icon: '\u23F1' },
                  { stat: '50%', label: 'Faster invoicing — bill on the day of delivery', icon: '\u26A1' },
                  { stat: '0', label: 'Check calls needed — automated status updates handle it', icon: '\u2705' },
                  { stat: '$0', label: 'Extra cost per user — unlimited users on every plan', icon: '\U0001F4B0' },
                ].map((item, i) => (
                  <Box key={i} sx={{ bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '16px', p: 3, display: 'flex', alignItems: 'center', gap: 3, transition: 'all 0.3s', '&:hover': { borderColor: C.accent, transform: 'translateX(4px)' } }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '1.5rem' }}>{item.icon}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: C.accentLight }}>{item.stat}</Typography>
                      <Typography sx={{ fontSize: '0.88rem', color: C.text3, lineHeight: 1.5 }}>{item.label}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ==========================================================
          LOAD TRACKING & INVOICING BENEFITS
          ========================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: C.sectionBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography sx={{ fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.72rem', color: C.accent, textTransform: 'uppercase', mb: 1.5 }}>Get Paid Faster</Typography>
            <Typography component="h2" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: C.white, lineHeight: 1.15 }}>
              Load Tracking & Invoicing{' '}
              <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>That Work Together</Box>
            </Typography>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px', p: { xs: 3, md: 4 }, height: '100%' }}>
                <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.15rem', color: C.white, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <VisibilityIcon sx={{ color: '#f59e0b', fontSize: 24 }} /> Real-Time Load Tracking
                </Typography>
                {[
                  'See every load\'s status from pickup to delivery on one screen',
                  'Drivers update status via WhatsApp — no app download required',
                  'Brokers and shippers receive automatic notifications at each milestone',
                  'Track on-time delivery rates and identify bottlenecks',
                  'Color-coded status indicators: Unassigned, Dispatched, In Transit, Delivered',
                  'Full load history and audit trail for every shipment',
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.7 }}>
                    <CheckIcon sx={{ color: '#f59e0b', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.9rem', color: C.text2, lineHeight: 1.5 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px', p: { xs: 3, md: 4 }, height: '100%' }}>
                <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.15rem', color: C.white, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InvoiceIcon sx={{ color: '#22c55e', fontSize: 24 }} /> Automated Invoicing
                </Typography>
                {[
                  'Invoices generated automatically the moment a load is delivered',
                  'Professional PDF invoices emailed directly to brokers and shippers',
                  'Aging dashboard shows 0-30, 31-60, 61-90, and 90+ day receivables',
                  'Track paid, pending, and overdue status at a glance',
                  'Reduce Days Sales Outstanding (DSO) and improve cash flow',
                  'No separate accounting software needed for basic billing',
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.7 }}>
                    <CheckIcon sx={{ color: '#22c55e', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.9rem', color: C.text2, lineHeight: 1.5 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="contained" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                borderRadius: '12px', px: 5, py: 1.5,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s',
              }}>
              Start Free Trial
            </Button>
          </Box>
        </Container>
      </Box>
      {/* --- FAQ --- */}
      <Box id="faq" sx={{ py: { xs: 8, md: 10 }, bgcolor: C.heroBg }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography component="h2" variant="h2" sx={{
              fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' },
              color: C.white, lineHeight: 1.15,
            }}>TMS for Small Fleets - Frequently Asked Questions</Typography>
          </Box>
          {[
            { q: 'Does this work for small carriers?', a: 'Yes - Cargo-Command is purpose-built for fleets with 1-25 trucks. No enterprise bloat, no modules you\'ll never use.' },
            { q: 'Any long contracts?', a: 'No - cancel anytime. We don\'t lock you in. Month-to-month billing, that\'s it.' },
            { q: 'Is setup included?', a: 'Yes - free onboarding support. Most teams are dispatching within a day.' },
            { q: 'Do you support EDI?', a: 'Yes - EDI 204, 990, 214, and 210 through Orderful. Available at no extra monthly cost on the Growth plan.' },
            { q: 'Is there a per-user fee?', a: 'Never. Unlimited users on every plan. Add dispatchers, managers, accountants - no extra cost.' },
            { q: 'Do you customize the product?', a: 'Absolutely. We tailor Cargo-Command to each customer\'s specific workflows and needs — your TMS should work the way you do, not the other way around.' },
            { q: 'What if you don\'t have functionality that I need?', a: 'We\'ll customize it to suit your needs. Whether it\'s accounting, MobileComm, EDI, ELD integrations, or something else entirely — we do it all.' },
            { q: 'I\'m a dispatcher — not a carrier. Is this for me?', a: `Absolutely. Our Dispatcher plan is built specifically for independent dispatchers managing multiple carriers. Track loads across fleets, manage commissions automatically, and handle invoicing — all from one dashboard for $${planPrices.dispatcher}/month.` },
            { q: 'Can I manage trucks from different carriers?', a: 'Yes. The Dispatcher plan includes multi-carrier management so you can organize trucks, drivers, and loads by carrier while keeping everything in one place.' },
            { q: 'How does commission tracking work?', a: 'Set a default commission rate (e.g. 5-10%) in your settings. Every load automatically calculates your commission, net revenue, and totals — no spreadsheets needed.' },
            { q: 'What if I have more than 25 trucks?', a: 'Contact us for custom pricing. We\'ll build a plan that fits your operation.' },
          ].map((faq, i) => (
            <FaqItem key={i} question={faq.q} answer={faq.a} />
          ))}

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="contained" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                borderRadius: '12px', px: 5, py: 1.5,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s',
              }}>
              Start Free Trial
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ==========================================================
          SEO CONTENT -- Long-form keyword-rich for search engines
          ========================================================== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: C.heroBg }}>
        <Container maxWidth="md">
          <Typography component="h2" variant="h2" sx={{
            fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' },
            color: C.white, lineHeight: 1.15, mb: 4, textAlign: 'center',
          }}>
            Why Small Trucking Companies Need a{' '}
            <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Purpose-Built TMS
            </Box>
          </Typography>

          <Box sx={{ color: C.text2, fontSize: '1rem', lineHeight: 1.85 }}>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Running a small trucking company with 1 to 20 trucks means wearing every hat &mdash; dispatcher, accountant, operations manager, and sometimes driver. Most transportation management systems on the market are designed for enterprise carriers with hundreds of trucks, forcing small fleets to pay for features they will never use while struggling with complex interfaces that slow down daily work.
            </Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Cargo-Command is different. We built our <Typography component="a" href="/tms-for-small-fleets" sx={{ color: C.accentLight, textDecoration: 'underline' }}>TMS for small fleets</Typography> because we know the unique challenges small carriers face: tight margins, limited office staff, and the need for software that works on day one without weeks of training.
            </Typography>

            <Typography component="h3" sx={{ color: C.white, fontWeight: 700, fontSize: '1.25rem', mt: 5, mb: 2 }}>Dispatch Software That Saves Hours Every Day</Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Our <Typography component="a" href="/dispatch-software" sx={{ color: C.accentLight, textDecoration: 'underline' }}>dispatch software</Typography> replaces the spreadsheets, whiteboards, and sticky notes that most small fleets rely on. With a centralized dispatch board, you can see every load, every driver, and every truck on a single screen. Assign loads with a click, track status updates in real time, and automatically notify brokers when loads are picked up and delivered.
            </Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              For <Typography component="a" href="/dispatch-software-for-owner-operators" sx={{ color: C.accentLight, textDecoration: 'underline' }}>owner-operators</Typography> scaling from one truck to five or ten, Cargo-Command grows with you. There are no per-user fees, so you can add dispatchers, accountants, and managers as your team grows without watching your software costs climb.
            </Typography>

            <Typography component="h3" sx={{ color: C.white, fontWeight: 700, fontSize: '1.25rem', mt: 5, mb: 2 }}>Invoicing and Billing for Trucking Companies</Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Late invoicing is one of the biggest cash flow killers for small carriers. With Cargo-Command&apos;s built-in <Typography component="a" href="/trucking-invoice-software" sx={{ color: C.accentLight, textDecoration: 'underline' }}>trucking invoice software</Typography>, you can auto-generate professional invoices the moment a load is delivered. Email them directly to brokers and shippers, track payment status at a glance, and see aging summaries so you always know who owes you money and how overdue it is.
            </Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              No more waiting days or weeks to invoice a load. Most of our customers cut their invoicing time by more than half in the first week.
            </Typography>

            <Typography component="h3" sx={{ color: C.white, fontWeight: 700, fontSize: '1.25rem', mt: 5, mb: 2 }}>Fleet Management for Small Carriers</Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              A <Typography component="a" href="/fleet-management-system" sx={{ color: C.accentLight, textDecoration: 'underline' }}>fleet management system</Typography> should give you clear visibility into your operation without burying you in data. Cargo-Command shows you where your drivers are, which trucks are available, and what your revenue looks like &mdash; all from a simple dashboard that loads in seconds.
            </Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Track Hours of Service compliance, manage driver information, and monitor truck assignments in one place. Whether you run 3 trucks or 20, you get the same straightforward experience with no hidden complexity.
            </Typography>

            <Typography component="h3" sx={{ color: C.white, fontWeight: 700, fontSize: '1.25rem', mt: 5, mb: 2 }}>EDI for Small Carriers &mdash; No Expensive Middleware</Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Brokers increasingly require <Typography component="a" href="/edi-for-small-carriers" sx={{ color: C.accentLight, textDecoration: 'underline' }}>EDI (Electronic Data Interchange)</Typography> for load tenders, status updates, and invoicing. Most EDI solutions are priced for large carriers, leaving small fleets locked out of broker requirements. Cargo-Command includes EDI 204, 990, 214, and 210 support on the Growth plan through Orderful at no additional monthly cost.
            </Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Accept load tenders electronically, send automated status updates, and transmit invoices &mdash; all without purchasing separate middleware or paying per-transaction fees.
            </Typography>

            <Typography component="h3" sx={{ color: C.white, fontWeight: 700, fontSize: '1.25rem', mt: 5, mb: 2 }}>Built in Murfreesboro, TN &mdash; Serving Fleets Nationwide</Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              Cargo-Command is based in Murfreesboro, Tennessee, in the heart of the Southeast trucking corridor. We understand the day-to-day reality of small carrier operations because we are part of the same community. Our customers range from single-truck owner-operators to 20-truck fleets hauling freight across the country.
            </Typography>
            <Typography component="p" sx={{ mb: 2.5 }}>
              With flat monthly pricing starting at $149 per month, free onboarding, and no long-term contracts, Cargo-Command is the affordable trucking TMS that helps small fleets dispatch smarter, invoice faster, and grow their business. <Typography component="a" href="/register" sx={{ color: C.accentLight, textDecoration: 'underline' }}>Start your free 14-day trial</Typography> and see the difference in your first week.
            </Typography>
          </Box>
        </Container>
      </Box>
      {/* ==========================================
          FINAL CTA BANNER
          ========================================== */}
      <Box sx={{
        py: { xs: 10, md: 14 }, bgcolor: C.sectionBg, textAlign: 'center',
        position: 'relative',
        '&::before': {
          content: '""', position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: '60%', height: '60%',
          background: 'radial-gradient(ellipse, rgba(37,99,235,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}>
        <Container maxWidth="md">
          <Typography component="h2" variant="h2" sx={{
            fontWeight: 800, fontSize: { xs: '2rem', md: '2.8rem' },
            color: C.white, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2,
          }}>
            Ready to simplify{' '}
            <Box component="span" sx={{ background: C.gradientText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              dispatch & billing?
            </Box>
          </Typography>
          <Typography component="p" sx={{ color: C.text3, fontSize: '1.05rem', mb: 5, maxWidth: 440, mx: 'auto' }}>
            14-Day Trial | $149/month | No credit card required
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" onClick={goRegister} endIcon={<ArrowIcon />}
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1.05rem',
                borderRadius: '12px', px: 5, py: 1.6,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s',
              }}>
              Start Free Trial
            </Button>
            <Button variant="outlined" size="large"
              onClick={() => openContact('Book Demo')}
              sx={{
                borderColor: '#cbd5e1', color: C.text1,
                textTransform: 'none', fontWeight: 600, fontSize: '1.05rem',
                borderRadius: '12px', px: 5, py: 1.6,
                '&:hover': { borderColor: C.accent, bgcolor: C.accentBg },
                transition: 'all 0.3s',
              }}>
              Book Demo
            </Button>
          </Box>
        </Container>
      </Box>

      {/* === FOOTER === */}
      <Box component="footer" sx={{ bgcolor: C.navBg, borderTop: `1px solid ${C.divider}`, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TruckIcon sx={{ color: C.accent, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: C.text2 }}>Cargo-Command</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: C.text3 }}>
              &copy; {new Date().getFullYear()} Cargo-Command. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Typography component="a" href="/privacy" sx={{ fontSize: '0.8rem', color: C.text3, textDecoration: 'none', '&:hover': { color: C.text2 }, transition: 'color 0.2s' }}>Privacy</Typography>
              <Typography component="a" href="/terms" sx={{ fontSize: '0.8rem', color: C.text3, textDecoration: 'none', '&:hover': { color: C.text2 }, transition: 'color 0.2s' }}>Terms</Typography>
              <Typography onClick={() => openContact('Website Contact')} sx={{ fontSize: '0.8rem', color: C.text3, cursor: 'pointer', '&:hover': { color: C.text2 }, transition: 'color 0.2s' }}>Contact</Typography>
              <IconButton component="a" href="https://www.facebook.com/profile.php?id=61559808975473" target="_blank" rel="noopener noreferrer" aria-label="Facebook" sx={{ color: C.text3, p: 0.5, '&:hover': { color: '#1877F2' }, transition: 'color 0.2s' }}><FacebookIcon sx={{ fontSize: 20 }} /></IconButton>
              <IconButton component="a" href="https://www.linkedin.com/company/cargo-command/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" sx={{ color: C.text3, p: 0.5, '&:hover': { color: '#0A66C2' }, transition: 'color 0.2s' }}><LinkedInIcon sx={{ fontSize: 20 }} /></IconButton>
            </Box>
          </Box>
          {/* Local SEO Signal */}
          <Box sx={{ textAlign: 'center', borderTop: `1px solid ${C.divider}`, pt: 2 }}>
            <Typography sx={{ fontSize: '0.75rem', color: C.text3, lineHeight: 1.8 }}>
              Based in Murfreesboro, TN - serving small fleets nationwide.
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: C.text3 }}>
              TMS for small fleets &nbsp;|&nbsp; Dispatch software for small trucking companies &nbsp;|&nbsp; Affordable trucking TMS
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* === AUTH DIALOG === */}
      <Dialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        TransitionComponent={Fade}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '20px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          },
        }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' } } }}
      >
        <Box sx={{ p: { xs: 3, sm: 4.5 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <TruckIcon sx={{ color: C.accent, fontSize: 36, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: C.white }}>
              {authMode === 'login' ? 'Welcome Back' : 'Start Your Free Trial'}
            </Typography>
            <Typography sx={{ color: C.text3, fontSize: '0.88rem', mt: 0.5 }}>
              {authMode === 'login' ? 'Sign in to your Cargo-Command account' : '14 days free — no credit card required'}
            </Typography>
          </Box>

          {authError && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{authError}</Alert>}

          <Box component="form" onSubmit={handleAuthSubmit}>
            {authMode === 'register' && (
              <>
                <TextField fullWidth label="Full Name" required autoFocus
                  value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', borderRadius: '10px', color: C.text1, '& fieldset': { borderColor: C.cardBorder }, '&:hover fieldset': { borderColor: C.accent }, '&.Mui-focused fieldset': { borderColor: C.accent } }, '& .MuiInputLabel-root': { color: C.text3 } }}
                />
                <TextField fullWidth label="Company Name" required
                  value={regForm.company} onChange={(e) => setRegForm({ ...regForm, company: e.target.value })}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', borderRadius: '10px', color: C.text1, '& fieldset': { borderColor: C.cardBorder }, '&:hover fieldset': { borderColor: C.accent }, '&.Mui-focused fieldset': { borderColor: C.accent } }, '& .MuiInputLabel-root': { color: C.text3 } }}
                />
              </>
            )}
            <TextField fullWidth label="Email Address" type="email" required
              autoFocus={authMode === 'login'}
              value={authMode === 'login' ? loginForm.email : regForm.email}
              onChange={(e) => authMode === 'login' ? setLoginForm({ ...loginForm, email: e.target.value }) : setRegForm({ ...regForm, email: e.target.value })}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', borderRadius: '10px', color: C.text1, '& fieldset': { borderColor: C.cardBorder }, '&:hover fieldset': { borderColor: C.accent }, '&.Mui-focused fieldset': { borderColor: C.accent } }, '& .MuiInputLabel-root': { color: C.text3 } }}
            />
            <TextField fullWidth label="Password" type="password" required
              value={authMode === 'login' ? loginForm.password : regForm.password}
              onChange={(e) => authMode === 'login' ? setLoginForm({ ...loginForm, password: e.target.value }) : setRegForm({ ...regForm, password: e.target.value })}
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', borderRadius: '10px', color: C.text1, '& fieldset': { borderColor: C.cardBorder }, '&:hover fieldset': { borderColor: C.accent }, '&.Mui-focused fieldset': { borderColor: C.accent } }, '& .MuiInputLabel-root': { color: C.text3 } }}
            />
            <Button type="submit" fullWidth variant="contained" disabled={authLoading} size="large"
              sx={{
                background: C.gradient, textTransform: 'none', fontWeight: 700, fontSize: '1.05rem',
                borderRadius: '12px', py: 1.5,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '&:hover': { boxShadow: '0 12px 44px rgba(37,99,235,0.5)' },
                '&.Mui-disabled': { bgcolor: 'rgba(37,99,235,0.3)', color: 'rgba(255,255,255,0.5)' },
              }}>
              {authLoading ? 'Please wait...' : authMode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2.5 }}>
            {authMode === 'login' && (
              <Typography
                component="a" href="/forgot-password"
                sx={{ fontSize: '0.82rem', color: C.text3, textDecoration: 'none', '&:hover': { color: C.accentLight }, display: 'block', mb: 1.5 }}
              >Forgot your password?</Typography>
            )}
            <Typography sx={{ fontSize: '0.88rem', color: C.text3 }}>
              {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <Typography
                component="span"
                onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }}
                sx={{ color: C.accentLight, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                {authMode === 'login' ? 'Start Free Trial' : 'Sign In'}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Dialog>
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} defaultSubject={contactSubject} />
    </Box>
  );
}
